import {
  IN_STOCK,
  LOW_STOCK,
  OUT_OF_STOCK,
} from '@jouzen/ecom-components/v3/consts/stockStatus';
import { RingFinishEnum } from '@jouzen/ecom-components/v3/enums/ringFinish';
import type { StockStatus } from '@jouzen/ecom-components/v3/types/stockStatus';
import { useDynamicConfig } from '@statsig/react-bindings';
import type { MessageKey } from 'types/LocaleMessageKey';

import { RingStyleEnum } from '@/app/enums/RingStyleEnum';

interface LegacyGen3SizeLevelInventoryStructure {
  size: number | string;
  sku: string;
  stockStatus: StockStatus;
}

interface LegacyGen3FinishLevelInventoryStructure {
  stockStatus: StockStatus | undefined;
  sizes: LegacyGen3SizeLevelInventoryStructure[];
}
interface LegacyStyleLevelInventoryStructure {
  stockStatus: StockStatus;
  [RingFinishEnum.Black]: LegacyGen3FinishLevelInventoryStructure;
  [RingFinishEnum.Silver]: LegacyGen3FinishLevelInventoryStructure;
  [RingFinishEnum.Stealth]: LegacyGen3FinishLevelInventoryStructure;
  [RingFinishEnum.Gold]: LegacyGen3FinishLevelInventoryStructure;
  [RingFinishEnum.BrushedSilver]: LegacyGen3FinishLevelInventoryStructure;
  [RingFinishEnum.Titanium]: LegacyGen3FinishLevelInventoryStructure;
  [RingFinishEnum.Rose]: LegacyGen3FinishLevelInventoryStructure;
  [RingFinishEnum.RoseGold]: LegacyGen3FinishLevelInventoryStructure;
}

export interface LegacyGen3InventoryStructure {
  OR: number;
  shippingDelayValue: number;
  lowStockMessage: MessageKey;
  outOfStockMessage: MessageKey;
  buttonCTA: MessageKey;
  [RingStyleEnum.Heritage]: LegacyStyleLevelInventoryStructure;
  [RingStyleEnum.Horizon]: LegacyStyleLevelInventoryStructure;
}

export type Gen3Style = RingStyleEnum.Heritage | RingStyleEnum.Horizon;

export type Gen3Finish =
  | RingFinishEnum.Black
  | RingFinishEnum.Silver
  | RingFinishEnum.Stealth
  | RingFinishEnum.Gold
  | RingFinishEnum.BrushedSilver
  | RingFinishEnum.Titanium
  | RingFinishEnum.Rose
  | RingFinishEnum.RoseGold;

const useLegacyGen3InventoryStructure = (): LegacyGen3InventoryStructure => {
  const inventoryBySku = useDynamicConfig(
    'inventory_insights_by_sku_pdp',
  ).value;

  // Create the base structure with default values
  const result: LegacyGen3InventoryStructure = {
    OR: 3,
    shippingDelayValue: 3,
    lowStockMessage: 'inventory_insights_low_stock_shop_gen4',
    outOfStockMessage: 'inventory_insights_out_of_stock_shop_gen4',
    buttonCTA: 'pdp_add_to_cart',
    [RingStyleEnum.Heritage]: {
      stockStatus: OUT_OF_STOCK,
      [RingFinishEnum.Black]: { stockStatus: OUT_OF_STOCK, sizes: [] },
      [RingFinishEnum.Silver]: { stockStatus: OUT_OF_STOCK, sizes: [] },
      [RingFinishEnum.Stealth]: { stockStatus: OUT_OF_STOCK, sizes: [] },
      [RingFinishEnum.Gold]: { stockStatus: OUT_OF_STOCK, sizes: [] },
      [RingFinishEnum.BrushedSilver]: {
        stockStatus: OUT_OF_STOCK,
        sizes: [],
      },
      [RingFinishEnum.Titanium]: { stockStatus: OUT_OF_STOCK, sizes: [] },
      [RingFinishEnum.Rose]: { stockStatus: OUT_OF_STOCK, sizes: [] },
      [RingFinishEnum.RoseGold]: { stockStatus: OUT_OF_STOCK, sizes: [] },
    },
    [RingStyleEnum.Horizon]: {
      stockStatus: OUT_OF_STOCK,
      [RingFinishEnum.Black]: { stockStatus: OUT_OF_STOCK, sizes: [] },
      [RingFinishEnum.Silver]: { stockStatus: OUT_OF_STOCK, sizes: [] },
      [RingFinishEnum.Stealth]: { stockStatus: OUT_OF_STOCK, sizes: [] },
      [RingFinishEnum.Gold]: { stockStatus: OUT_OF_STOCK, sizes: [] },
      [RingFinishEnum.BrushedSilver]: {
        stockStatus: OUT_OF_STOCK,
        sizes: [],
      },
      [RingFinishEnum.Titanium]: { stockStatus: OUT_OF_STOCK, sizes: [] },
      [RingFinishEnum.Rose]: { stockStatus: OUT_OF_STOCK, sizes: [] },
      [RingFinishEnum.RoseGold]: { stockStatus: OUT_OF_STOCK, sizes: [] },
    },
  };

  const mappings: Record<string, { style: Gen3Style; finish: Gen3Finish }> = {
    // Heritage mappings
    '1001': {
      style: RingStyleEnum.Heritage,
      finish: RingFinishEnum.Black,
    },
    '1003': {
      style: RingStyleEnum.Heritage,
      finish: RingFinishEnum.Silver,
    },
    '1004': {
      style: RingStyleEnum.Heritage,
      finish: RingFinishEnum.Stealth,
    },
    '1002': {
      style: RingStyleEnum.Heritage,
      finish: RingFinishEnum.Gold,
    },
    // Horizon mappings
    '51382': {
      style: RingStyleEnum.Horizon,
      finish: RingFinishEnum.Black,
    },
    '51384': {
      style: RingStyleEnum.Horizon,
      finish: RingFinishEnum.Silver,
    },
    '51385': {
      style: RingStyleEnum.Horizon,
      finish: RingFinishEnum.Stealth,
    },
    '51383': {
      style: RingStyleEnum.Horizon,
      finish: RingFinishEnum.Gold,
    },
    '52594': {
      style: RingStyleEnum.Horizon,
      finish: RingFinishEnum.Titanium,
    },
    '51386': {
      style: RingStyleEnum.Horizon,
      finish: RingFinishEnum.Rose,
    },
  };
  //List of the Gen3 style/finish combinations that are valid SKUs
  const validSkus = Object.keys(mappings);

  // Helper function to map the middle section (ie: JZ90-[styleFinishCode]-[size]) of the sku to its according style and finish
  const getStyleAndFinish = (
    styleFinishCode: string,
  ): { style: Gen3Style; finish: Gen3Finish } | null => {
    return mappings[styleFinishCode] ?? null;
  };

  // Helper function to convert inventory status to stock status typing
  const getStockStatus = (inventoryData: unknown): StockStatus => {
    if (inventoryData == 'inStock') {
      return IN_STOCK;
    }
    if (inventoryData == 'lowStock') {
      return LOW_STOCK;
    }
    return OUT_OF_STOCK;
  };

  // Helper to process finish-level SKUs
  const processFinishSku = (
    style: Gen3Style,
    finish: Gen3Finish,
    configStockStatus: StockStatus,
  ) => {
    if (result[style][finish].stockStatus !== configStockStatus) {
      result[style][finish].stockStatus = configStockStatus;
    }

    if (
      configStockStatus !== OUT_OF_STOCK &&
      result[style].stockStatus === OUT_OF_STOCK
    ) {
      result[style].stockStatus = configStockStatus;
    }
  };

  // Helper to process size-level SKUs
  const processSizeSku = ({
    sku,
    style,
    finish,
    sizeStr,
    stockStatus,
  }: {
    sku: string;
    style: Gen3Style;
    finish: Gen3Finish;
    sizeStr: string;
    stockStatus: StockStatus;
  }) => {
    if (result[style][finish].stockStatus !== IN_STOCK) {
      return;
    }
    const size = parseInt(sizeStr, 10);
    if (!isNaN(size)) {
      result[style][finish].sizes.push({ size, sku, stockStatus });
    }
  };

  // Helper function to process each inventory entry
  function processInventoryEntry(sku: string, inventoryData: unknown) {
    const parts = sku.split('-');
    if (parts.length < 2) return;
    if (!parts[1]) return;
    const styleFinishCode = parts[1];

    //This filters out the invalid skus from being processed -> dexcom chargers or4 ceramic etc
    if (!validSkus.includes(styleFinishCode) || parts[0] !== 'JZ90') return;

    const styleFinish = getStyleAndFinish(styleFinishCode);
    if (!styleFinish) return;

    const { style, finish } = styleFinish;
    const stockStatus = getStockStatus(inventoryData);

    if (parts.length === 2) {
      processFinishSku(style, finish, stockStatus);
    } else if (parts.length === 3) {
      const sizeStr = parts[2];
      if (sizeStr) {
        processSizeSku({ sku, style, finish, sizeStr, stockStatus });
      }
    }
  }

  // Process inventory and map to result structure
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (inventoryBySku && typeof inventoryBySku === 'object') {
    Object.entries(inventoryBySku).forEach(([sku, inventoryData]) => {
      processInventoryEntry(sku, inventoryData);
    });
  }

  // Set any undefined finish stock statuses to OUT_OF_STOCK
  const styles: Gen3Style[] = [RingStyleEnum.Heritage, RingStyleEnum.Horizon];
  const finishes: Gen3Finish[] = [
    RingFinishEnum.Black,
    RingFinishEnum.Silver,
    RingFinishEnum.Stealth,
    RingFinishEnum.Gold,
    RingFinishEnum.BrushedSilver,
    RingFinishEnum.Titanium,
    RingFinishEnum.Rose,
    RingFinishEnum.RoseGold,
  ];

  styles.forEach((style) => {
    finishes.forEach((finish) => {
      if (result[style][finish].stockStatus === undefined) {
        result[style][finish].stockStatus = OUT_OF_STOCK;
      }
    });
  });

  return result;
};

export default useLegacyGen3InventoryStructure;
